import React, { useState, useEffect, useCallback, useRef } from "react";
import TranslationTable from "./TranslationTable";
import { logMsg } from "../../common/Logger";
import NewComponentTranslation from "./NewComponentTranslation";
import { Link, MenuItem } from "@mui/material";
import styles from "./Translation.module.scss";
import TranslationsClient from "./TranslationsClient";
import FormControl from "@mui/material/FormControl";
import { KeyboardArrowDown } from "@mui/icons-material";
import subNavStyles from "../../shared/subnav/subNavStyles";
import Select from "@mui/material/Select";
import {
  LANCODES,
  MARKET_LANGUAGES,
  TRANSLATION_TOOL_REGIONS,
} from "../../common/Constants";

function TranslationDetails(props) {
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [addNewComponent, setAddNewComponent] = useState(false);
  const [disableAddComponent, setDisableAddComponent] = useState(true);
  const [showNewComponentTranslation, setShowNewComponentTranslation] = useState(true);
  const [translation, setTranslation] = useState([]);
  const [currentLan, setCurrentLan] = useState(null);
  const [fetchFailed, setFetchFailed] = useState(false);

  const marketLanRef = useRef(null);

  const pickUpStyledSelectSx = {
    "& .MuiSelect-select": {
      paddingTop: "5px",
      paddingBottom: "5px",
      paddingLeft: "10px",
      paddingRight: "10px",
      width: "calc(100% - 34px)",
      boxSizing: "border-box",
      fontSize: 14,
      color: "#102b4e",
      lineHeight: 1.57,
      letterSpacing: 1.5,
      fontFamily: "FordAntenna-Regular, sans-serif",
      "&:focus": {
        background: "transparent",
      },
    },
  };

  const pickUpStyledMenuItemSx = {
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "10px",
    paddingRight: "10px",
    margin: 0,
    fontSize: 14,
    color: "#102b4e",
    lineHeight: 1.57,
    letterSpacing: 1.5,
    fontFamily: "FordAntenna-Regular, sans-serif",
  };

  const pickUpFormControlSx = {
    width: 150,
  };

  const getLangDropDown = useCallback(() => {
    marketLanRef.current = MARKET_LANGUAGES[props.user.market];
    const newCurrentLan = props.localizedInfo.defaultLanguage.isocode.substr(0, 2);
    setCurrentLan(newCurrentLan);
  }, [props.user.market, props.localizedInfo.defaultLanguage.isocode]);

  const getTranslations = useCallback((lanCode) => {
    props.hideOrShow(true);
    setIsDataLoaded(false);
    TranslationsClient.getTranslationsLanguage(
      lanCode,
      props.userDetails.market
    )
      .then((response) => {
        const translationArray = [];
        setTranslation(Object.assign({ ...translationArray }, { ...response }));
      })
      .catch((error) => {
        logMsg("the error is: ", error);
        setFetchFailed(true);
      })
      .finally(() => {
        props.hideOrShow(false);
        setIsDataLoaded(true);
      });
  }, [props]);

  const getMarketsForNewTranslations = useCallback(() => {
    for (const key in TRANSLATION_TOOL_REGIONS) {
      if (
        TRANSLATION_TOOL_REGIONS[key].includes(props.userDetails.market)
      ) {
        return TRANSLATION_TOOL_REGIONS[key];
      }
    }
    return null;
  }, [props.userDetails.market]);

  const onClickAddComponent = useCallback(() => {
    setDisableAddComponent(false);
    setAddNewComponent(true);
    setShowNewComponentTranslation(true);
  }, []);

  const handleInputChange = useCallback((event) => {
    if (
      event.target.value !== currentLan &&
      event.target.value !== undefined
    ) {
      setCurrentLan(event.target.value);
      getTranslations(event.target.value);
    }
  }, [currentLan, getTranslations]);

  const addTranslationComponent = useCallback((updatedLanguage) => {
    props.hideOrShow(true);
    TranslationsClient.postAddComponentTranslations(
      updatedLanguage,
      getMarketsForNewTranslations()
    )
      .then(() => {
        getTranslations(currentLan);
      })
      .finally(() => {
        props.hideOrShow(false);
        setDisableAddComponent(true);
        setShowNewComponentTranslation(false);
      });
  }, [props, getMarketsForNewTranslations, getTranslations, currentLan]);

  const showNewComponentTranslationToggle = useCallback(() => {
    setShowNewComponentTranslation(prev => !prev);
  }, []);

  const cancelTranslation = useCallback(() => {
    setDisableAddComponent(true);
    setAddNewComponent(false);
    setShowNewComponentTranslation(false);
  }, []);

  useEffect(() => {
    getLangDropDown();
    getTranslations(
      props.localizedInfo.defaultLanguage.isocode.substr(0, 2)
    );
  }, [getLangDropDown, getTranslations, props.localizedInfo.defaultLanguage.isocode]);

  if (fetchFailed) {
    return (
      <div className={styles.errorText}>
        <h2>{props.t("FetchError.errorText")}</h2>
      </div>
    );
  }

  return (
    <div>
      {isDataLoaded && (
        <div>
          {marketLanRef.current && (
            <div className={styles.dropdownDiv}>
              <span className={styles.inputLabel}>
                {props.t("Translations.translationLanguage")}
              </span>

              <FormControl fullWidth={false} sx={pickUpFormControlSx}>
                <Select
                  sx={pickUpStyledSelectSx}
                  IconComponent={() => (
                    <KeyboardArrowDown style={subNavStyles.icon} />
                  )}
                  name="langcode"
                  id="pickUplang"
                  data-testid="langDropDown"
                  className={styles.dropdown}
                  value={currentLan}
                  onClick={handleInputChange}
                >
                  {marketLanRef.current &&
                    marketLanRef.current.map((state) => {
                      return (
                        <MenuItem
                          key={state}
                          value={state}
                          sx={pickUpStyledMenuItemSx}
                        >
                          <div>{LANCODES[state]}</div>
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
            </div>
          )}
          {translation.translations && Object.keys(translation.translations).length !== 0 &&
            Object.keys(translation.translations).map((id) => {
              return (
                <TranslationTable
                  {...props}
                  currentLan={currentLan}
                  languageSelected={props.localizedInfo.defaultLanguage.isocode.substr(
                    0,
                    2
                  )}
                  translations={translation.translations[id]}
                  key={translation.translations[id].moduleKey}
                  getTranslations={getTranslations}
                  getMarketsForNewTranslations={getMarketsForNewTranslations}
                />
              );
            })}
        </div>
      )}
      <br />

      <div>
        <div style={{ position: "center" }}>
          {disableAddComponent && (
            <Link
              className={styles.newTranslation}
              style={{ color: "#102b4e" }}
              variant="contained"
              onClick={onClickAddComponent}
            >
              {props.t("Translations.addNewComponent")}
            </Link>
          )}
        </div>
        {addNewComponent && (
          <NewComponentTranslation
            {...props}
            showNewComponentTranslation={showNewComponentTranslationToggle}
            showNewComponentTranslationvalue={showNewComponentTranslation}
            currentLan={currentLan}
            languageSelected={props.localizedInfo.defaultLanguage.isocode.substr(
              0,
              2
            )}
            addTranslationComponent={addTranslationComponent}
            cancelTranslation={cancelTranslation}
          />
        )}
      </div>
    </div>
  );
}

export default TranslationDetails;
